import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import {
  submitProblem,
  setProblem,
  setMapData,
  clearMapData,
  increaseUserUsage,
  recordUsageEvent,
  USAGE_EVENTS,
  getRoutingTourInfo,
  setSolutionJson,
  setLoader,
  selectUnassigned,
  setShowProblem,
  TIMEOUT_VALUES,
  setTourParameter,
  selectTourById,
  setUserParam,
  clearTourParameter,
  clearPudoGroups,
  clearParkings,
  clearReloads,
  clearLocations,
  setTerritoriesFromProblemFile,
  setDisplayParam,
  clearLimits,
} from 'actions';
import { last, isEmpty, isEqual } from 'lodash';
import { generateVRPPayload, convertParsedCSVToVRPJobs, mergeJobs } from 'utils/VRPConverter';
import { applyJsonPudoDurations } from 'utils/map/MarkerDragHandler';
import { convertProblemToGeoJSON } from 'utils/GeoJSONConverter';
import { useCookies } from 'react-cookie';
import { getLastValidIteration } from 'utils/SolutionHelpers';
import { calcReloadCapacity, calcTotalDemand } from 'utils/reloadHelpers';
import { clearAll } from 'utils/apiFilesUploadHelpers';
import { defaultValue } from 'reducers/tourPlanner';
import {
  generateAvoidAreasFromFileProblem,
  generateExcludeAreasFromFileProblem,
  generatePolygonsFromOrders,
} from 'utils/territories/TerritoriesFromJson';
import WizardHeader from './Global/WizardHeader';
import WizardFooter from './Global/WizardFooter';
import { Step1Container } from './Step1/Step1Container';
import Step3Container from './Step3/Step3Container';
import Step4Container from './Step4/Step4Container';
import { AmplitudeService, AMPLITUDE_EVENTS } from '../../utils/amplitude';
import Notifications from './Global/Notifications';
import { getClusters } from '../../utils/csv/clusters';
import { tourToRoutingRequest } from '../../utils/RoutingConverter';
import { isSolutionMonoTour } from '../../utils/MemoryHelpers';
import { getSafeValue } from '../../utils/security';
import IdSearchContainer from './Step5/IdSearchContainer';
import { canDownloadCSV, isProdEnv } from '../../utils/helpers';
import { colors } from '../../global/variables';
import Step2Container from './Step2/Step2Container';
import { getInitialStep } from '../../utils/WizardHelpers';

const isProd = isProdEnv();

const StyledContainer = styled.div`
  height: 100%;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
`;

const StyledWizardContent = styled.div`
  position: relative;
  height: ${(props) => (!props.noHeader ? 'calc(100% - 9rem)' : 'calc(100% - 4.5rem)')};
  box-sizing: border-box;
  overflow-y: ${(props) => (props.scrollable ? 'auto' : 'hidden')};
  overflow-x: hidden;
  z-index: 200;
`;

const { secondaryColor, primaryColorHovered } = colors;

const bgImage = `linear-gradient(
    to right,
    ${secondaryColor} 0%,
    ${primaryColorHovered} 100%
  )`;

export const StyledTourDispatcherContainer = styled.div(
  ({ searchOnly, step4, static: isStatic }) => ({
    position: isStatic ? 'static' : 'absolute',
    top: step4 ? '0.5rem' : '1.2rem',
    right: step4 ? '0.5rem' : '1.2rem',
    backgroundImage: bgImage,
    borderRadius: '0.2rem',
    width: searchOnly ? '2.5rem' : canDownloadCSV() ? '15rem' : '12.5rem',
  }),
);

export const StyledActions = styled.div(({ searchOnly }) => ({
  height: searchOnly ? '2rem' : '2.7rem',
  margin: 'auto',
  div: {
    display: 'inline-block',
  },
}));
//
const STEPS_IN_HEADER = 3;

const Wizard = ({
  user,
  oAuth,
  tourData,
  tourPlanner,
  display,
  mapData,
  setWizardShown,
  wizardShown,
}) => {
  const dispatch = useDispatch();
  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const territories = useSelector(({ areas: stateAreas }) => stateAreas[solution.show].territories);
  const orders = useSelector((state) => state.orders[solution.show]);
  const fullTourPlanner = useSelector(({ tourPlanner: stateTourPlanner }) => stateTourPlanner);
  const reloads = useSelector((s) => s.reloads);
  const pudoGroups = useSelector((s) => s.pudoGroups);
  const parkings = useSelector((s) => s.parkings);

  const ordersNotLocated = useSelector((state) => state.ordersNotLocated);
  const errorState = useSelector((state) => state.error);
  const uploadedFile = useSelector((state) => state.uploadedFile);
  const handleSubmitProblem = useCallback(
    (problem) => dispatch(submitProblem(problem)),
    [dispatch],
  );
  const lastImport = user.lastImport;
  const csvOrders = lastImport?.filename?.endsWith('.csv') && !isEmpty(lastImport.delimiter);
  const hasUnassigned = tourData?.unassigned;
  const handleSetLoader = useCallback((data) => dispatch(setLoader(data)), [dispatch]);
  const handleIncreaseUsage = useCallback((data) => dispatch(increaseUserUsage(data)), [dispatch]);
  const handleSetProblem = useCallback((problem) => dispatch(setProblem(problem)), [dispatch]);
  const handleSetTourParameter = useCallback(
    (parameter) => dispatch(setTourParameter({ ...parameter, userChange: false })),
    [dispatch],
  );
  const handleSetSolutionJson = useCallback(
    (param) => dispatch(setSolutionJson(param)),
    [dispatch],
  );
  const handleSetMapData = useCallback(
    (mapDataLocal) => dispatch(setMapData(mapDataLocal)),
    [dispatch],
  );
  const handleClearMapData = useCallback(() => dispatch(clearMapData()), [dispatch]);
  const handleRecordUsage = useCallback((data) => dispatch(recordUsageEvent(data)), [dispatch]);
  const handleSelectTourById = useCallback((data) => dispatch(selectTourById(data)), [dispatch]);
  const handleSetDisplayParameter = useCallback(
    (data) => dispatch(setDisplayParam(data)),
    [dispatch],
  );
  const tempOrders = solution.requests[solution.show].tempOrders;
  const hasOrdersRef = useRef(isEmpty(orders) ? tempOrders : orders);
  const lstIter = last(solution.requests[solution.show].iterations);
  const iterationRef = useRef(lstIter);
  const hideRoutes = user.mapSettings.hideAllRoutes;

  const handleGetRoutingTourInfo = useCallback(
    (index, tour) =>
      dispatch(
        getRoutingTourInfo({
          oAuth,
          routingRequest: tourToRoutingRequest(
            tour,
            tourPlanner,
            tempOrders || orders,
            null,
            hideRoutes,
          ),
          tourId: index,
          solutionId: solution.show,
        }),
      ),
    [dispatch, oAuth, tourPlanner, solution.show, orders, tempOrders, hideRoutes],
  );
  const handleSelectUnassigned = useCallback(
    (value) => dispatch(selectUnassigned(value)),
    [dispatch],
  );
  const handleClearTourParameter = useCallback(
    (parameter) => dispatch(clearTourParameter(parameter, solution.show)),
    [dispatch, solution.show],
  );
  const handleSetShowProblem = useCallback((value) => dispatch(setShowProblem(value)), [dispatch]);
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);

  const wizardContentRef = useRef(null);
  const tourPlannerRef = useRef(tourPlanner);
  const territoriesRef = useRef(territories);
  const reloadsRef = useRef(reloads);
  const pudoGroupsRef = useRef(pudoGroups);
  const parkingsRef = useRef(parkings);
  const prevJsonTriggerVRPRef = useRef(solution.jsonTriggerVRP);
  const hadJobsRef = useRef(false);
  const triggerVRPRequestRef = useRef(null);

  useEffect(() => {
    tourPlannerRef.current = tourPlanner;
    territoriesRef.current = territories;
    reloadsRef.current = reloads;
    pudoGroupsRef.current = pudoGroups;
    parkingsRef.current = parkings;
  }, [tourPlanner, territories, reloads, pudoGroups, parkings]);

  const { routeIds, sharedRouteIds } = display;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(getInitialStep(solution));
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [backToStep, setBackToStep] = useState(3);
  const [restore, setRestore] = useState(false);
  const [searchSelectedTour, setSearchSelectedTour] = useState(false);
  const [shouldUpdateValues, setShouldUpdateValues] = useState(false);
  const [step5, setStep5] = useState();
  const [cookies] = useCookies(['apikey']);
  const notices = solution.requests[solution.show]?.notices;
  const unassigned = solution.requests[solution.show]?.unassigned;
  const unassignedSol = tourData?.tours.length === 0 && (notices || unassigned);
  const vehicleLimits = user.mapSettings.addVehicleLimit;
  const highlightStop = user.highlightStop?.id;

  const handleSetStep = useCallback(
    (toStep, setLoading = true) => {
      setStep(toStep);
      if (setLoading) handleSetLoader({ isLoading: false });
      handleRecordUsage({ event: USAGE_EVENTS.WIZARD_SET_STEP, step: toStep });
      wizardContentRef.current.scrollTo(0, 0);
    },
    [setStep, handleRecordUsage, handleSetLoader],
  );

  const triggerVRPRequest = useCallback(
    (allOrders, config, triggerVrp, shouldntUpdate, jsonManual) => {
      const lastIteration = last(solution.requests[solution.show].iterations);
      if (
        !triggerVrp &&
        ((config.fileType === 'json' &&
          allOrders?.length > 0 &&
          solution &&
          !triggerVrp &&
          !isEmpty(solution.requests[solution.show].iterations)) ||
          getLastValidIteration(last(solution.requests)))
      ) {
        return lastIteration.request;
      }

      const fileName = solution.requests[solution.show].name;
      const wizardDepot = !isEmpty(config.location.value) ? config.location.value : null;
      const wizardReturnLocation = !isEmpty(config.returnLocation)
        ? config.returnLocation.value
        : null;
      const returnLocationAddress = !isEmpty(config.returnLocation)
        ? config.returnLocation.label
        : null;
      const rows = convertParsedCSVToVRPJobs(allOrders, false, territories.areaDetails);

      const mergedJobs = mergeJobs(rows);

      const jsonPlanGroups = lastIteration?.request?.json?.plan?.groups;
      const effectivePudoGroups = applyJsonPudoDurations(pudoGroupsRef.current, jsonPlanGroups);

      const vrpPayload = generateVRPPayload(
        mergedJobs,
        wizardDepot,
        '',
        config,
        allOrders,
        territories,
        user,
        triggerVrp,
        jsonManual,
        solution.json,
        reloadsRef.current,
        effectivePudoGroups,
        parkingsRef.current,
        jsonPlanGroups,
      );
      const depotAddress =
        (config.location.value && config.location.value.lat && config.location.label) ||
        (allOrders && allOrders[0] && allOrders[0].Address);
      const toSet = {
        territories: territories.areaDetails,
        tourPlanner: config,
        orders: allOrders,
        json: vrpPayload,
        uploaded: true,
      };

      if (triggerVrp && !shouldntUpdate) {
        toSet.name = fileName;
        toSet.index = solution.show;
        const { territories: terr = [], groupAreas = [] } = generatePolygonsFromOrders(orders);
        const avoidAreas =
          generateAvoidAreasFromFileProblem(lastIteration?.request?.json, orders) || [];
        const excludeAreas =
          generateExcludeAreasFromFileProblem(lastIteration?.request?.json, orders) || [];
        dispatch(
          setTerritoriesFromProblemFile({
            groupAreas,
            territories: terr,
            avoidAreas,
            excludeAreas,
          }),
        );
      }

      const clusters = getClusters(allOrders, config);
      if (clusters) {
        const rowsCluster = convertParsedCSVToVRPJobs(clusters, true, territories.areaDetails);
        toSet.clusters = clusters;
        toSet.index = solution.show;
        toSet.json = generateVRPPayload(
          rowsCluster,
          wizardDepot,
          '',
          config,
          allOrders,
          territories,
          user,
        );
      }

      handleSetProblem(toSet);
      if (triggerVrp && vrpPayload) {
        handleSetUserParam({ currentJson: vrpPayload });
      }
      const geo = convertProblemToGeoJSON(
        vrpPayload,
        wizardDepot,
        wizardReturnLocation,
        allOrders,
        depotAddress,
        returnLocationAddress,
        config.editedOrder,
        territories.areaDetails,
        reloadsRef.current.length
          ? reloadsRef.current.map(({ lat, lng, duration }) => ({
              location: { lat, lng },
              duration,
            }))
          : undefined,
        parkingsRef.current,
        config,
      );
      if (geo) {
        handleSetMapData({ geo });
      } else {
        handleClearMapData();
      }
      handleSetSolutionJson({ jsonTriggerVRP: false, jsonManual: false });
      return toSet;
    },
    [
      handleSetMapData,
      handleClearMapData,
      handleSetProblem,
      handleSetUserParam,
      solution,
      territories,
      handleSetSolutionJson,
      user,
    ],
  );

  useEffect(() => {
    triggerVRPRequestRef.current = triggerVRPRequest;
  }, [triggerVRPRequest]);

  useEffect(() => {
    const allIterations =
      solution && solution.requests[0].iterations ? solution.requests[0].iterations : null;
    const lastIteration = allIterations ? last(allIterations) : null;

    if (lastIteration && !lastIteration.isProcessing) {
      setIsSubmitting(false);
    }
  }, [solution]);

  const wizardSubmitProblem = useCallback(
    (vrpReq, timeout = TIMEOUT_VALUES.MEDIUM) => {
      if (!vrpReq) return;
      if (!orders || !orders.length) return;

      const apiKey = cookies.apikey;
      setIsSubmitting(true);
      setTimeout(() => {
        handleSubmitProblem({
          ...vrpReq,
          user,
          oAuth,
          apiKey,
          keepOrders: tourPlanner.ordersMode !== 'json_import',
        });
      }, timeout);
    },
    [
      setIsSubmitting,
      handleSubmitProblem,
      user,
      orders,
      oAuth,
      cookies.apikey,
      tourPlanner.ordersMode,
    ],
  );

  const onStepChange = useCallback(
    (toStep, isStep5, unassignedTour) => {
      let finalStep = toStep;
      setStep5(isStep5);

      if (toStep === 2 && (step === 3 || step === 4)) {
        handleSetSolutionJson({ jsonNoMapMovement: true });
        handleSetDisplayParameter({ noMapMovement: true });
      }
      if (toStep === 2 || toStep === 1) handleSetSolutionJson({ jsonTriggerVRP: true });

      if (toStep < step) {
        handleSetSolutionJson({
          json: false,
          jsonUserChange: false,
        });

        setRestore(false);
      }
      handleRecordUsage({ event: USAGE_EVENTS.WIZARD_SET_STEP, step: toStep });

      if (step === 2) {
        if (toStep < step) {
          if (solution.jsonPending && tourPlanner.ordersMode === 'json_import') {
            clearAll(
              dispatch,
              true,
              true,
              solution.show,
              tourPlanner.uploadedLocations,
              tourPlanner.vehicles[0]?.limits,
            );
          }
          dispatch(clearLimits());
          dispatch(clearLocations({ userChange: tourPlanner.userChange }));
        }
        if (toStep === 3 || toStep === 4) {
          const config = { ...tourPlanner };
          delete config.editedOrder;
          if (reloads.length > 0) {
            const totalDemand = calcTotalDemand(orders || []);
            const currentCapacity = config.vehicles?.[0]?.capacity;
            const numReloads = reloads.length;
            config.vehicles = config.vehicles.map((v, i) => {
              if (i !== 0) return v;
              const updated = { ...v, amount: 1 };
              if (totalDemand > 0 && Array.isArray(currentCapacity) && currentCapacity.length > 0) {
                updated.capacity = calcReloadCapacity(currentCapacity, totalDemand, numReloads);
              }
              return updated;
            });
          }
          const vrpReq = triggerVRPRequest(orders, config, true);
          wizardSubmitProblem(vrpReq);
          handleIncreaseUsage({ toursPlanned: 1 });
          AmplitudeService.log(AMPLITUDE_EVENTS.TOUR_PLAN, {
            isDemo: user.isLastOrdersAddedDemo,
            orderNumber: orders.length,
          });
          return;
        }
      } else if (step === 3) {
        if (toStep < step) {
          clearAll(
            dispatch,
            false,
            false,
            solution.show,
            tourPlanner.uploadedLocations,
            null,
            csvOrders,
          );
          if (tourPlanner.fileType === 'json' && tourPlanner.ordersMode === 'json_import') {
            handleClearTourParameter(['reloads', 'groups']);
            dispatch(clearPudoGroups());
            dispatch(clearParkings());
            dispatch(clearReloads());
            handleClearMapData();
          }
        }
      } else if (step === 4) {
        if (toStep < step) {
          const isMonoSolution = isSolutionMonoTour(tourData);
          if (isMonoSolution) {
            clearAll(
              dispatch,
              false,
              false,
              solution.show,
              tourPlanner.uploadedLocations,
              null,
              csvOrders,
            );
            if (tourPlanner.fileType === 'json' && tourPlanner.ordersMode === 'json_import') {
              handleClearTourParameter(['reloads', 'groups']);
              dispatch(clearPudoGroups());
              dispatch(clearParkings());
              dispatch(clearReloads());
              handleClearMapData();
            }
          }
          finalStep = isMonoSolution ? 2 : 3;
        }
      }
      if ([1, 2].includes(finalStep) && [2, 3, 4].includes(step)) setShouldUpdateValues(true);
      if (finalStep === 3 && step === 4) handleSelectTourById({ selectedTour: undefined });
      if (unassignedTour) {
        handleSetSolutionJson({
          json: true,
          jsonUserChange: true,
          jsonPending: true,
          jsonUnassigned: true,
        });
      }

      handleSetStep(finalStep);
    },
    [
      user,
      step,
      wizardSubmitProblem,
      handleSetStep,
      orders,
      tourPlanner,
      tourData,
      triggerVRPRequest,
      handleIncreaseUsage,
      dispatch,
      handleSetSolutionJson,
      handleRecordUsage,
      solution.show,
    ],
  );

  const onClickTour = useCallback(
    ({ previous }) => {
      const current = display.selectedTour;
      handleSetUserParam({ intersectionCount: null, overlapCount: null, highlightStop: null });
      if (current === undefined || !tourData) return;
      const next = previous
        ? current <= 0
          ? 0
          : current - 1
        : current >= tourData.tours.length - 1
        ? 0
        : current + 1;

      handleSelectTourById({ selectedTour: next });
      handleSelectTourById({ selectOne: next });
      handleGetRoutingTourInfo(next, getSafeValue(tourData.tours, next));
    },
    [display.selectedTour, tourData, handleSelectTourById, handleGetRoutingTourInfo],
  );

  const onPanelToggle = useCallback(
    (isVisible) => {
      setIsPanelVisible(isVisible);
    },
    [setIsPanelVisible],
  );

  const onSearchId = useCallback(() => handleSetStep(5), []);

  const request = getLastValidIteration(solution.requests[solution.show])?.request;

  useEffect(() => {
    const triggerJsonVRP = solution.jsonTriggerVRP;
    const noMovement = solution.jsonNoMapMovement;
    const jsonManual = solution.jsonManual;
    const ords = isEmpty(orders) ? tempOrders : orders;

    if (prevJsonTriggerVRPRef.current === true && triggerJsonVRP === false) {
      prevJsonTriggerVRPRef.current = triggerJsonVRP;
      return undefined;
    }

    prevJsonTriggerVRPRef.current = triggerJsonVRP;

    if ((!errorState && !tourData && !solution.json) || triggerJsonVRP) {
      const timer = setTimeout(() => {
        triggerVRPRequestRef.current(
          ords,
          tourPlannerRef.current,
          triggerJsonVRP,
          null,
          jsonManual,
        );
      }, 100);
      if (!noMovement)
        handleSelectTourById({ selectedTour: display.selectedTour, noMapMovement: false });
      else handleSetDisplayParameter({ noMapMovement: true });

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [
    orders,
    tourPlanner,
    tourData,
    errorState,
    territories,
    solution.json,
    solution.jsonTriggerVRP,
    solution.jsonNoMapMovement,
    solution.jsonManual,
    handleSetSolutionJson,
    handleSetDisplayParameter,
    display.selectedTour,
    tempOrders,
  ]);

  useEffect(() => {
    if (
      step5 ||
      !tourData ||
      !tourData.tours ||
      !last(solution.requests[solution.show].iterations).response
    )
      return;
    const toStep = solution.jsonNoTours
      ? 2
      : !hasUnassigned && isSolutionMonoTour(tourData)
      ? 4
      : 3;
    handleSetStep(toStep, false);
  }, [tourData, solution.requests, solution.show, step5, hasUnassigned]);

  useEffect(() => {
    if (step === 3 || step === 4) {
      setBackToStep(step);
      setSearchSelectedTour(step === 4);
    }
  }, [step]);

  useEffect(() => {
    if (display.selectedTour !== undefined && step === 3 && !hasUnassigned) handleSetStep(4);
  }, [display.selectedTour, step, handleSetStep, oAuth, hasUnassigned]);

  const onTourChange = useCallback(() => handleSetStep(4), [handleSetStep]);
  const showHeader = step <= STEPS_IN_HEADER;
  const hasSolution = !!tourData;
  const showStep3 = (step === 3 && request && hasSolution) || (unassignedSol && tourData);

  useEffect(() => {
    if (step !== 1 && step !== 2 && !hasSolution) {
      handleSetStep(2);
    }
  }, [hasSolution, step, handleSetStep]);

  useEffect(() => {
    if (solution.json) {
      handleSelectUnassigned(false);
      handleSetShowProblem(true);
      handleSetStep(2, false);
    }
  }, [solution.json, handleSetStep, handleSelectUnassigned, handleSetShowProblem]);

  useEffect(() => {
    if ((step === 3 || step === 4) && solution.jsonPaste) {
      onStepChange(2);
    }
  }, [step, solution.jsonPaste, solution.show, onStepChange]);

  useEffect(() => {
    if (highlightStop && highlightStop !== -1) {
      handleSetStep(4, false);
    }
  }, [highlightStop]);

  useEffect(() => {
    if (errorState) {
      setShouldUpdateValues(true);
    }
  }, [errorState]);

  const hasNoApiKey = isProd && (!cookies.apikey || cookies.apikey === '');

  useEffect(() => {
    if (
      tourPlanner.userChange === false &&
      shouldUpdateValues &&
      !vehicleLimits &&
      !solution.jsonPaste
    ) {
      const vehicles = fullTourPlanner.temp?.vehicles ?? defaultValue.vehicles;
      const vehicleProfiles = fullTourPlanner.temp?.vehicleProfiles ?? defaultValue.vehicleProfiles;
      const location = fullTourPlanner.temp?.location ?? defaultValue.location;
      const returnLocation = fullTourPlanner.temp?.returnLocation ?? defaultValue.returnLocation;
      const returnLocationMode =
        fullTourPlanner.temp?.returnLocationMode ?? defaultValue.returnLocationMode;
      handleSetTourParameter({
        vehicles,
        vehicleProfiles,
        location,
        returnLocation,
        returnLocationMode,
      });
      setShouldUpdateValues(false);
    }
  }, [
    fullTourPlanner.temp,
    handleSetTourParameter,
    tourPlanner.userChange,
    shouldUpdateValues,
    vehicleLimits,
    solution.jsonPaste,
  ]);

  useEffect(() => {
    const lastReq = last(solution.requests[solution.show].iterations);
    const jobCount = lastReq?.request?.json?.plan?.jobs.length;
    const emptyFile = jobCount === 0;

    const wasNonEmpty = hadJobsRef.current;
    if (jobCount > 0) hadJobsRef.current = true;

    if (emptyFile && wasNonEmpty && !restore) {
      const manualBreaks = (tourPlannerRef.current?.breaks || []).filter((b) => b.isManual);
      if (manualBreaks.length > 0) {
        handleSetTourParameter({ breaks: manualBreaks });
        handleClearTourParameter(['recharges', 'start', 'end', 'restTimes', 'types']);
      } else {
        handleClearTourParameter(['breaks', 'recharges', 'start', 'end', 'restTimes', 'types']);
      }

      setRestore(true);
      hadJobsRef.current = false;
    }
  }, [
    handleClearTourParameter,
    handleSetTourParameter,
    restore,
    solution.requests,
    solution.show,
  ]);

  useEffect(() => {
    if (
      isEmpty(orders) &&
      !solution.requests[solution.show].tempOrders &&
      !solution.jsonNoTerritories
    )
      dispatch(
        setTerritoriesFromProblemFile({
          groupAreas: [],
          territories: [],
          avoidAreas: [],
          excludeAreas: [],
        }),
      );
  }, [orders, solution.requests[solution.show].tempOrders, solution.jsonNoTerritories]);

  useEffect(() => {
    if (solution.jsonWizard) {
      handleSetSolutionJson({
        jsonWizard: false,
        json: false,
        jsonPending: false,
        jsonUserChange: false,
      });
    }
  }, [solution.jsonWizard]);

  useEffect(() => {
    if (solution.jsonNoTours) {
      console.warn('triggering step 2 due to no tours in json');
      handleSetStep(2);
    }
  }, [solution.jsonNoTours]);

  useEffect(() => {
    const ords = isEmpty(orders) ? tempOrders : orders;
    const lastIteration = last(solution.requests[solution.show].iterations);

    if (
      !isEmpty(ords) &&
      (!isEqual(hasOrdersRef.current, ords) || !isEqual(iterationRef.current, lastIteration)) &&
      !solution.jsonNoTerritories
    ) {
      const { territories: terr = [], groupAreas = [] } = generatePolygonsFromOrders(ords);
      const avoidAreas = generateAvoidAreasFromFileProblem(lastIteration?.request.json, ords) || [];
      const excludeAreas =
        generateExcludeAreasFromFileProblem(lastIteration?.request.json, ords) || [];
      dispatch(
        setTerritoriesFromProblemFile({
          groupAreas,
          territories: terr,
          avoidAreas,
          excludeAreas,
          fromUploadedFile: false,
        }),
      );
      hasOrdersRef.current = ords;
      iterationRef.current = lastIteration;
    }
  }, [orders, solution.requests, solution.show, dispatch, tempOrders, solution.jsonNoTerritories]);

  return (
    <StyledContainer>
      <Notifications user={user} />
      {showHeader && (
        <WizardHeader
          step={step}
          onChange={onStepChange}
          hasSolution={hasSolution}
          isPanelVisible={isPanelVisible}
          setWizardShown={setWizardShown}
          wizardShown={wizardShown}
        />
      )}
      <StyledWizardContent
        noHeader={!showHeader}
        ref={wizardContentRef}
        scrollable={showHeader || step === 5}
      >
        {step === 1 && (
          <Step1Container
            oAuth={oAuth}
            tourPlanner={tourPlanner}
            user={user}
            onTerritoryPlanningPanelToggle={onPanelToggle}
            mapData={mapData}
            solution={solution}
          />
        )}
        {step === 2 && (
          <Step2Container
            orders={orders}
            ordersNotLocated={ordersNotLocated}
            errorState={errorState}
            tourPlanner={tourPlanner}
            oAuth={oAuth}
            user={user}
            solution={solution}
            uploadedFile={uploadedFile}
            onConfigToggle={onPanelToggle}
            setIsDemoMode={(value) => setIsDemoMode(value)}
          />
        )}
        {showStep3 && (
          <Step3Container
            user={user}
            oAuth={oAuth}
            tourData={tourData}
            solution={solution}
            onTourChange={onTourChange}
            selectedTours={routeIds}
            sharedTours={sharedRouteIds}
            orders={orders}
            tourPlanner={tourPlanner}
            display={display}
            onSearchId={onSearchId}
            selectTour={handleSelectTourById}
          />
        )}
        {step === 4 && request && (
          <Step4Container
            tourData={tourData}
            solution={solution}
            selectedTours={routeIds}
            tourPlanner={tourPlanner}
            user={user}
            orders={orders}
            onSearchId={onSearchId}
            oAuth={oAuth}
          />
        )}
        {step === 5 && (
          <IdSearchContainer
            tourData={tourData}
            selectedTours={routeIds}
            searchSelectedTour={searchSelectedTour}
          />
        )}
      </StyledWizardContent>
      <WizardFooter
        step={step}
        onClick={onStepChange}
        onClickTour={onClickTour}
        isSubmitting={isSubmitting}
        orders={orders}
        tourData={tourData}
        oAuth={oAuth}
        tourPlanner={tourPlanner}
        disabled={isPanelVisible}
        hasNoApiKey={hasNoApiKey}
        isDemoMode={isDemoMode}
        backToStep={backToStep}
        triggerVRPRequest={triggerVRPRequest}
        currentSelectedTour={display.selectedTour}
        solution={solution}
        user={user}
      />
    </StyledContainer>
  );
};

export default Wizard;
